import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {LocalStorage} from '../../../utils/LocalStorage';
import {MCP_API_URL} from '../../../settings/local_settings';
import {copyPlainText} from '../../../utils/copyPlainText';
import {attributeHelpCenterClick, prefetchHelpCenterIdentity} from '../../../utils/HelpCenterAttribution';
import {usePreviewContext} from '../../../context/PreviewContext';
import {ChatGptCommonErrors, CodeBlock, OptionalPromptStep, OptionalSkillStep, Step} from './SetupGuideCard';

// Artículo del Centro de Ayuda con el paso a paso de ChatGPT ilustrado (el de acá es el resumen).
// Va hardcodeado porque es UN link a contenido editorial, no una integración. Sólo en `/es/`: la
// versión `/en/` del artículo no existe (404, verificado el 05/10).
export const CHATGPT_HELP_ARTICLE_URL = 'https://ayuda.dashbo.io/es/articles/a-50-conecta-dashbo-con-chatgpt';

// Los mismos asistentes que el paso de la IA del túnel (`StepConnectAI.ASSISTANTS`), en el mismo
// orden y con las mismas etiquetas: es «el lenguaje del onboarding» que pidió la card eUkO5X6S. Sin
// «No tengo ninguna»: acá no hay recorrido que cerrar. Gemini no se ofrece (card CYzf5yVV).
export const ASSISTANTS = ['claude', 'claude_code', 'chatgpt', 'other'];
const HAS_HINT = ['claude', 'claude_code'];
const COPIED_MS = 2000;

// El isotipo para el campo de ícono de ChatGPT. Su URL de producción la usan también los artículos
// del Centro de Ayuda: si se mueve, cambiarlo allá.
const DASHBO_ICON_URL = `${process.env.PUBLIC_URL || ''}/dashbo-icono.png`;

function initialAssistant() {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('assistant');
        return ASSISTANTS.includes(fromUrl) ? fromUrl : 'claude';
    } catch {
        return 'claude';
    }
}

/** Una clave con los menús en negrita: HTML propio, sin interpolar nada del usuario ni del backend. */
function Html({k}) {
    const {t} = useTranslation();
    return <span dangerouslySetInnerHTML={{__html: t(k)}}/>;
}

/**
 * La dirección del conector con «Copiar». Hay que pegarla en otra aplicación, así que transcribirla a
 * mano es el camino más probable al error, y una URL mal tipeada no falla acá: falla del otro lado y
 * sin decir por qué. «Copiado» vuelve solo, y sólo se dice si se copió (`copyPlainText`).
 */
function ConnectorUrl({url}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const copy = () => {
        copyPlainText(url).then(() => {
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        }, () => setCopied(false));
    };

    return (
        <span className="mcp-connect__url">
            <code>{url}</code>
            <button type="button" className="btn -alt -negative btn-sm" onClick={copy}>
                <FaIcon iconName="copy"/> {copied ? t('dashbo_mcp.connect.copied_url') : t('dashbo_mcp.connect.copy_url')}
            </button>
        </span>
    );
}

function ClaudeSteps({url, prompts, copyStatus, onCopied}) {
    const {t} = useTranslation();
    return (
        <>
            <Step number={1}><Html k="onboarding.ia.step1.claude"/></Step>
            <Step number={2}>
                <Html k="dashbo_mcp.connect.claude_step2"/>
                <ConnectorUrl url={url}/>
            </Step>
            {/* Los dos pasos que faltaban (capacitación de Not ordinary agency, 05/10): qué opción
                elegir al registrar el conector, y dar el permiso para siempre en vez de aprobar
                cada llamada. */}
            <Step number={3}><Html k="dashbo_mcp.connect.claude_step3"/></Step>
            <Step number={4}><Html k="dashbo_mcp.connect.claude_step4"/></Step>
            <Step number={5}><Html k="dashbo_mcp.connect.claude_step5"/></Step>
            <Step number={6}>{t('dashbo_mcp.connect.claude_step6')}</Step>
            <OptionalPromptStep prompts={prompts} copyStatus={copyStatus} onCopied={onCopied}>
                {t('dashbo_mcp.setup.optional_prompt_hint_claude_desktop')}
            </OptionalPromptStep>
        </>
    );
}

/**
 * ChatGPT: los pasos detallados de la card vieja, que son los que sigue el artículo a-50. El paso 1
 * es la MISMA clave que el túnel (hasta 2026-10 eran dos textos y éste mandaba al «+» de Ajustes →
 * Complementos, que no existe: feedback de Hesta).
 */
function ChatGptSteps({url, prompts, copyStatus, onCopied}) {
    const {t} = useTranslation();
    useEffect(() => { prefetchHelpCenterIdentity(); }, []);
    return (
        <>
            {/* Con ícono y en ámbar, como en la card vieja: «desde el celular no se puede conectar» es
                lo primero que hay que saber, no una nota al pie (review C-…-25). */}
            <div className="setup-guide-note">
                <FaIcon iconName="exclamation-triangle"/> {t('dashbo_mcp.setup.chatgpt_note')}
            </div>
            <Step number={1}><Html k="onboarding.ia.step1.chatgpt"/></Step>
            <Step number={2}>
                {t('dashbo_mcp.setup.chatgpt_step2')}
                <ConnectorUrl url={url}/>
                <span className="mcp-connect__hint">
                    <a href={DASHBO_ICON_URL} download="dashbo-icono.png">
                        <FaIcon iconName="download"/> {t('onboarding.ia.logo_download')}
                    </a>
                    {' '}{t('onboarding.ia.logo_hint')}
                </span>
            </Step>
            <Step number={3}>{t('dashbo_mcp.setup.chatgpt_step3')}</Step>
            <Step number={4}>{t('dashbo_mcp.setup.chatgpt_step4')}</Step>
            <Step number={5}>{t('dashbo_mcp.setup.chatgpt_step5')}</Step>
            <OptionalPromptStep prompts={prompts} copyStatus={copyStatus} onCopied={onCopied}>
                {t('dashbo_mcp.setup.optional_prompt_hint_chatgpt')}
            </OptionalPromptStep>
            <ChatGptCommonErrors/>
            <p className="setup-guide-help-link">
                {/* Con la atribución del Centro de Ayuda (quién lo abre y desde qué pantalla), como
                    `LinkToArticle`: sin ella el clic salía anónimo (review C-…-26). */}
                <a href={CHATGPT_HELP_ARTICLE_URL} data-help-url={CHATGPT_HELP_ARTICLE_URL} target="_blank"
                   rel="noopener noreferrer" onMouseDown={attributeHelpCenterClick} onClick={attributeHelpCenterClick}>
                    <FaIcon iconName="circle-question"/> {t('dashbo_mcp.setup.chatgpt_help_article')}
                </a>
            </p>
        </>
    );
}

// Lo que ve la agencia en «Ver portal como cliente» en lugar de su token.
export const PREVIEW_TOKEN_PLACEHOLDER = '<token-del-cliente>';

/**
 * Claude Code no tiene pantalla de conectores: se agrega con un comando que lleva el token.
 *
 * En «Ver portal como cliente» el token de quien mira es el de la AGENCIA, que alcanza a todos sus
 * clientes: ese comando no se imprime con el token vivo, para que no termine copiado y pasado a un
 * cliente. El cliente, en su portal, ve el comando con el suyo (review C-…-09).
 */
function ClaudeCodeSteps({url}) {
    const {t} = useTranslation();
    const {isPreview} = usePreviewContext();
    const token = isPreview ? PREVIEW_TOKEN_PLACEHOLDER : LocalStorage.fetchUserToken();
    return (
        <>
            <Step number={1}>
                {t('dashbo_mcp.setup.claude_code_step1')}
                {/* Sin `--` antes de `--header`: ese separador es para transporte stdio y hace que
                    Claude Code DESCARTE el header en silencio ("Needs authentication"). */}
                <CodeBlock>{`claude mcp add -s user dashbo --transport http ${url} --header "Authorization: Bearer ${token}"`}</CodeBlock>
                <span className="mcp-connect__hint">
                    <FaIcon iconName="exclamation-triangle"/> {t(isPreview ? 'dashbo_mcp.connect.preview_token_note'
                                                                           : 'dashbo_mcp.connect.claude_code_warning')}
                </span>
            </Step>
            <Step number={2}>{t('dashbo_mcp.setup.claude_code_step2')}</Step>
            <OptionalSkillStep/>
        </>
    );
}

function OtherSteps({url}) {
    const {t} = useTranslation();
    return (
        <>
            <Step number={1}>{t('onboarding.ia.step1.other')}</Step>
            <Step number={2}>
                {t('dashbo_mcp.connect.other_step2')}
                <ConnectorUrl url={url}/>
            </Step>
            <Step number={3}>{t('dashbo_mcp.connect.login')}</Step>
        </>
    );
}

// Qué plan hace falta de verdad (las mismas frases que el túnel). La de ChatGPT va como aviso arriba de
// sus pasos (`ChatGptSteps`), porque además dice que desde el celular no se conecta.
const REQUIREMENT_KEYS = {
    claude: 'onboarding.ia.requirement.claude',
    claude_code: 'onboarding.ia.requirement.claude_code',
};

/**
 * «Conectar tu asistente», arriba de todo en `/dashbo-mcp` (card eUkO5X6S, recorrido de la 2.1 del
 * 02/10): la pantalla vieja abría con el token, las URLs y siete acordeones, y el que sólo quería su
 * IA conectada tenía que encontrar su caso entre Make y Python. Acá se elige el asistente y se ven
 * SUS pasos; lo técnico vive en «Configuración avanzada», más abajo.
 */
export function ConnectAssistantSection({prompts, copyStatus, onCopied}) {
    const {t} = useTranslation();
    // `?assistant=claude_code`: el túnel manda acá a quien eligió Claude Code, y tiene que llegar a
    // SUS pasos, no a los de Claude.
    const [assistant, setAssistant] = useState(initialAssistant);
    const url = `${MCP_API_URL}/mcp`;

    const stepsByAssistant = {
        claude: <ClaudeSteps url={url} prompts={prompts} copyStatus={copyStatus} onCopied={onCopied}/>,
        chatgpt: <ChatGptSteps url={url} prompts={prompts} copyStatus={copyStatus} onCopied={onCopied}/>,
        claude_code: <ClaudeCodeSteps url={url}/>,
        other: <OtherSteps url={url}/>,
    };

    return (
        <section className="dashbo-mcp-section mcp-connect">
            <h2 className="section-title">{t('dashbo_mcp.connect.title')}</h2>
            <p className="section-description">{t('dashbo_mcp.connect.subtitle')}</p>

            <div className="mcp-connect__assistants" role="radiogroup" aria-label={t('onboarding.ia.pick_assistant')}>
                {ASSISTANTS.map(each => (
                    <button type="button" key={each} role="radio" aria-checked={assistant === each}
                            className={`mcp-connect__assistant${assistant === each ? ' -on' : ''}`}
                            onClick={() => setAssistant(each)}>
                        {assistant === each && <FaIcon iconName="check"/>}
                        <span className="mcp-connect__name">{t(`onboarding.ia.assistants.${each}`)}</span>
                        {HAS_HINT.includes(each) && (
                            <span className="mcp-connect__chip-hint">{t(`onboarding.ia.assistants.${each}_hint`)}</span>
                        )}
                        {each === 'claude' && (
                            <span className="mcp-connect__tag">{t('onboarding.ia.recommended')}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Dentro de `.setup-guide-card .card-body` para heredar los pasos, el bloque de código y
                los colapsables de las guías de abajo: un solo estilo de paso en toda la pantalla. */}
            <div className="setup-guide-card mcp-connect__card">
                <div className="card-body" data-assistant={assistant}>
                    {stepsByAssistant[assistant]}
                </div>
            </div>

            {REQUIREMENT_KEYS[assistant] && (
                <p className="mcp-connect__requirement">{t(REQUIREMENT_KEYS[assistant])}</p>
            )}
        </section>
    );
}
